import ArticlePageLayout from '@/components/feature/ArticlePageLayout';
import { Link } from 'react-router-dom';

const pageData = {
  breadcrumbTitle: 'Sugargoo Warehouse Guide',
  category: 'Guide',
  title: 'Sugargoo Warehouse Guide: QC Photos, Storage & Processing 2026',
  excerpt: 'Understanding how the Sugargoo warehouse works — from item arrival and quality inspection to storage time limits and international package consolidation.',
  readTime: '9 min read',
  heroImageUrl: 'https://readdy.ai/api/search-image?query=Clean%20organized%20warehouse%20interior%20with%20labeled%20storage%20racks%20and%20packages%2C%20warm%20cream%20walls%2C%20soft%20industrial%20lighting%2C%20modern%20logistics%20facility%2C%20minimal%20aesthetic%2C%20architectural%20interior%20photography&width=1200&height=514&seq=article-hero-warehouse&orientation=landscape',
  heroImageAlt: 'Sugargoo warehouse guide - QC photos and storage process',
  schemaData: {
    headline: 'Sugargoo Warehouse Guide: QC Photos, Storage & Processing 2026',
    description: 'Understanding how the Sugargoo warehouse works — from item arrival and quality inspection to storage time limits and international package consolidation.',
    datePublished: '2026-08-04',
    dateModified: '2026-08-04',
  },
};

export default function ArticleWarehouse() {
  return (
    <ArticlePageLayout
      breadcrumbTitle={pageData.breadcrumbTitle}
      category={pageData.category}
      title={pageData.title}
      excerpt={pageData.excerpt}
      readTime={pageData.readTime}
      heroImageUrl={pageData.heroImageUrl}
      heroImageAlt={pageData.heroImageAlt}
      schemaData={pageData.schemaData}
    >
      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          How the Sugargoo Warehouse Process Works
        </h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          The <strong className="text-foreground-800">Sugargoo warehouse</strong> is the backbone of the entire shopping agent experience. Located in China, this facility serves as the intermediary between Chinese sellers and international buyers. Understanding how the warehouse operates helps you make informed decisions about ordering, storage, and shipping — especially when working with curated <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> collections.
        </p>
      </section>

      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Step-by-Step Warehouse Process
        </h2>
        <ol className="space-y-4 list-decimal pl-5">
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Seller Ships to Warehouse</strong>
            After you place an order through Sugargoo, the Chinese seller dispatches the item to the <strong className="text-foreground-800">Sugargoo warehouse</strong>. Domestic shipping within China typically takes 2-5 days. You can track this progress in your Sugargoo dashboard.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Warehouse Receiving</strong>
            Upon arrival, warehouse staff scan the package into the system. The status updates from &quot;Purchased&quot; to &quot;Warehouse Received&quot; in your dashboard, usually within a few hours of physical delivery.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">QC Photo Session</strong>
            Staff photograph the item from multiple angles — front, back, sides, tags, logos, and any distinctive details. The default set includes 3-5 photos. Additional specific-angle photos can be requested for a small fee (1-3 CNY each). Photos are uploaded within 24 hours.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Storage Period</strong>
            After QC, items enter your personal <strong className="text-foreground-800">Sugargoo warehouse</strong> storage. The free storage period is typically 90-180 days, giving you generous time to accumulate items from multiple <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> finds before shipping internationally.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800 block mb-1">Package Consolidation</strong>
            When ready, select stored items for consolidation. Warehouse staff pack items together, optimizing for space and protection. This is when you can request box removal, vacuum sealing, or other packaging preferences.
          </li>
        </ol>
      </section>

      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Understanding QC Photos: What to Look For
        </h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          QC photos are the most valuable feature of the <strong className="text-foreground-800">Sugargoo warehouse</strong> process. When reviewing photos, pay attention to:
        </p>
        <ul className="space-y-2 list-disc pl-5 text-sm text-foreground-600">
          <li><strong className="text-foreground-700">Color Accuracy:</strong> Compare against seller listing photos for color discrepancies</li>
          <li><strong className="text-foreground-700">Logo/Tag Details:</strong> Verify branding matches expectations</li>
          <li><strong className="text-foreground-700">Visible Defects:</strong> Stains, loose threads, scratches, or damage</li>
          <li><strong className="text-foreground-700">Size Labels:</strong> Confirm the size tag matches your order</li>
          <li><strong className="text-foreground-700">Material Appearance:</strong> Does the fabric texture look right?</li>
        </ul>
      </section>

      <section>
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
          Warehouse Storage Tips for Sugargoo Spreadsheet Shoppers
        </h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Smart storage management is a key skill for <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> enthusiasts. Here are practical tips:
        </p>
        <ul className="space-y-3 list-disc pl-5">
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800">Build hauls over time:</strong> Use the generous storage period to accumulate items from different spreadsheet finds before consolidating into one shipment.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800">Track storage deadlines:</strong> Note when each item&apos;s free storage expires. Sugargoo may charge storage fees after the free period.
          </li>
          <li className="text-sm md:text-base text-foreground-600 leading-relaxed">
            <strong className="text-foreground-800">Group similar items:</strong> Consolidate clothing items together and shoes together for optimal packing density.
          </li>
        </ul>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          For more on the buying process, see our <Link to="/article/how-to-use-sugargoo-spreadsheet" className="text-primary-500 hover:text-primary-600 font-medium">beginner tutorial</Link> and the <Link to="/article/sugargoo-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium">complete guide</Link>.
        </p>
      </section>
    </ArticlePageLayout>
  );
}